import classNames from 'classnames';
import { memo, useState } from 'react';

import { WIZE_SUBSCRIPTION_NAME } from 'my-core/constants';
import { formatDate, formatDateRange, isSameDate } from 'my-core/date-utils';
import { hasContent } from 'my-core/editor-utils';
import {
  getIsStudySessionExpired,
  getStudySessionDateDateStatus,
  getStudySessionDateStatus,
  getStudySessionIncludedFeatures,
  getStudySessionInstructor,
  getStudySessionStartTime,
  isStudySessionUserRegistered,
  STUDY_SESSION_TYPES,
} from 'my-core/study-session-utils';
import { getUserDisplayName } from 'my-core/user-utils';
import { numberToPrice } from 'my-utils';

import Editor, { pluginPresets } from 'my-editor';
import Label from 'my-elements/Label';
import MuiAvatar from 'my-elements/MuiAvatar';

import StudySessionRegisteredActions from '../StudySessionRegisteredActions';
import StudySessionCardActionButton from './StudySessionCardActionButton';
import StudySessionCardReferral from './StudySessionCardReferral';
import Timeline from './StudySessionCardTimeline';

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { faCalendarCheck, faCheck, faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ breakpoints, palette, shape, spacing }) => ({
  display: 'flex',
  '.CPSS-paper': {
    padding: spacing(2),
    borderRadius: shape.borderRadiusSm,
    flex: 1,
  },
  '.CPSS-card': {
    display: 'flex',
  },
  '.CPSS-card_small': { flexDirection: 'column' },
  '.CPSS-cardContent': { flex: 1 },
  '.CPSS-dateLabel': {
    marginBottom: spacing(1),
  },
  '.CPSS-heading_secondary': { color: palette.secondary.main },
  '.CPSS-heading_cyan': { color: palette.cyan.main },
  '.CPSS-heading_purple': { color: palette.purple.main },
  '.CPSS-heading_green': { color: palette.green.main },
  '.CPSS-heading_yellow': { color: palette.yellow.medium },
  '.CPSS-inclusions': {
    display: 'flex',
    margin: spacing(2, 0),
    flexWrap: 'wrap',
    '& > *': {
      marginRight: spacing(2),
      '& > svg': { marginRight: spacing(1) },
    },
  },
  '.CPSS-datePart': {
    display: 'flex',
    margin: spacing(1, 0),
    '& > *': { marginRight: spacing(1) },
    '& > svg': { marginTop: 3, color: palette.text.secondary },
  },
  '.CPSS-datePart_past': {
    '&, & > svg': { color: palette.text.disabled },
  },
  '.CPSS-icon': {
    marginRight: spacing(1),
  },
  '.CPSS-instructorWrapper': {
    display: 'flex',
    alignItems: 'center',
    margin: spacing(2, 0, 0),
    '& > *': { marginRight: spacing(1) },
  },
  '.CPSS-ratingIcon': {
    color: palette.yellow.main,
    marginRight: spacing(1),
  },
  '.CPSS-actions': {
    flex: '0 0 200px',
    display: 'flex',
    borderLeft: `1px solid ${palette.divider}`,
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    padding: spacing(2),
    margin: spacing(0, 0, 0, 2),
    '& > * + *': { marginTop: spacing(1) },
  },
  '.CPSS-priceBlock': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  '.CPSS-price': {
    display: 'flex',
    alignItems: 'baseline',
    '& > * + *': { marginLeft: spacing(1) },
  },
  '.CPSS-extraDetails': {
    marginTop: spacing(2),
    borderTop: `1px solid ${palette.divider}`,
    paddingTop: spacing(3),
  },
  [breakpoints.down('md')]: {
    '.CPSS-card': {
      width: '100%',
      flexDirection: 'column',
    },
    '.CPSS-actions': {
      width: '100%',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderLeft: 'none',
      borderTop: `1px solid ${palette.divider}`,
      margin: spacing(1, 0, 0),
      padding: spacing(2, 0, 0),
      flex: 0,
      '& > * + *': { marginTop: 0 },
    },
  },
});

export default memo(function CoursePageStudySessionCard(props) {
  const { className, course, loading, rootRef, smallCard, studySession, timelineArrow, timelineFirst, timelineLast } =
    props;

  const [expanded, setExpanded] = useState(false);

  const studySessionUser = studySession?.current_user_study_session_user;
  const registered = isStudySessionUserRegistered(studySessionUser);
  const startTime = getStudySessionStartTime(studySession);
  const typeConfig = STUDY_SESSION_TYPES[studySession?.type];
  const dateStatus = getStudySessionDateStatus(studySession);
  const isExpired = getIsStudySessionExpired(studySession, studySessionUser);

  const instructor = getStudySessionInstructor(studySession);

  const small = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));
  const isSmall = smallCard || small;

  const sessionIsToday = startTime && isSameDate(startTime, null, 'day');

  const showRegistreredActions = !loading && registered && !isExpired;

  const timelineText =
    studySession?.type === 'weekly_tutorial' ? 'Weekly'
    : sessionIsToday ? 'Today'
    : startTime ? formatDate(startTime, 'upcomingDate')
    : 'On-Demand';

  return (
    <div css={rootCss} ref={rootRef}>
      {!isSmall && renderTimeline()}
      {renderCard()}
    </div>
  );

  function renderTimeline() {
    if (loading) return <Timeline arrow={timelineArrow} first={timelineFirst} last={timelineLast} loading />;

    return (
      <Timeline
        arrow={timelineArrow}
        first={timelineFirst}
        isToday={sessionIsToday}
        last={timelineLast}
        text={timelineText}
      />
    );
  }

  function renderCard() {
    return (
      <Paper className={classNames(className, 'CPSS-paper')} elevation={5}>
        <div
          className={classNames('CPSS-card', {
            'CPSS-card_small': smallCard,
          })}
        >
          <div className="CPSS-cardContent">
            {isSmall && <Label className="CPSS-dateLabel" label={timelineText} variant="light" />}
            <Typography
              className={classNames({
                'CPSS-heading_secondary': typeConfig?.color === 'secondary',
                'CPSS-heading_cyan': typeConfig?.color === 'cyan',
                'CPSS-heading_purple': typeConfig?.color === 'purple',
                'CPSS-heading_green': typeConfig?.color === 'green',
                'CPSS-heading_yellow': typeConfig?.color === 'yellow',
              })}
              gutterBottom
              variant="subtitle1"
            >
              {loading ?
                <Skeleton variant="text" width={100} />
              : [smallCard && course?.code, studySession.title || typeConfig.label].filter(Boolean).join(': ')}
            </Typography>
            {loading ?
              [...Array(3)].map((_, i) => <Skeleton key={i} variant="text" width={`${Math.random() * 50 + 50}%`} />)
            : renderDescription()}
            {showRegistreredActions && renderRegisteredActions()}
            {loading ?
              <Skeleton variant="text" width={50} />
            : (
              (studySession.type === 'weekly_tutorial' && studySession.dates.length) ||
              hasContent(studySession.extra_details)
            ) ?
              <Button onClick={() => setExpanded(!expanded)} sx={{ mt: 1 }}>{`${expanded ? 'Hide' : 'View'} ${
                studySession.type === 'weekly_tutorial' ? `All ${studySession.dates.length} Sessions` : 'Details'
              }`}</Button>
            : null}
          </div>

          {isSmall && expanded && renderExtraDetails()}

          {!smallCard && (
            <div className="CPSS-actions">
              {loading ?
                <Skeleton height={60} variant="text" width={100} />
              : <>
                  {!registered && !isExpired && renderPriceBlock()}
                  <StudySessionCardActionButton {...props} isExpired={isExpired} />
                </>
              }
            </div>
          )}
        </div>
        {!isSmall && expanded && renderExtraDetails()}
        {registered && studySession.purchase_type === 'standard' && !!studySession.price && (
          <StudySessionCardReferral />
        )}
      </Paper>
    );
  }

  function renderDescription() {
    return (
      <div>
        <Typography
          component="div"
          gutterBottom
          sx={{ color: isExpired ? 'text.disabled' : 'text.primary' }}
          variant="body1"
        >
          {hasContent(studySession.description) ?
            <Editor plugins={pluginPresets.ALL} readOnly value={studySession.description} />
          : typeConfig?.description}
        </Typography>
        {!registered && (
          <div className="CPSS-inclusions">
            {getStudySessionIncludedFeatures(studySession).map(({ key, label }) => renderInclusion(key, label))}
            {!studySession.disable_zoom &&
              (dateStatus === 'live' || dateStatus === 'upcoming') &&
              renderInclusion('live_session', 'Live Online Session')}
          </div>
        )}

        {studySession.type !== 'weekly_tutorial' &&
          studySession.dates.map((ssd, idx) => {
            const dateStatus = getStudySessionDateDateStatus(ssd, studySession);
            return (
              <Typography
                key={ssd.id}
                className={classNames('CPSS-datePart', { 'CPSS-datePart_past': dateStatus === 'past' })}
                variant="body1"
              >
                <strong>Part {idx + 1}</strong>
                <FontAwesomeIcon icon={faCalendar} />
                <span>{formatDateRange(ssd.start_time, ssd.end_time, 'upcomingDateAndTime')}</span>
              </Typography>
            );
          })}

        {!loading && instructor && (
          <div className="CPSS-instructorWrapper">
            <MuiAvatar size={40} user={instructor} />
            <Typography noWrap sx={{ color: isExpired ? 'text.disabled' : 'text.primary' }} variant="subtitle1">
              {getUserDisplayName(instructor)}
            </Typography>
            {instructor.instructor_stats?.feedbacks?.average_rating >= 4 && (
              <Typography sx={{ color: isExpired ? 'text.disabled' : 'text.secondary' }} variant="subtitle1">
                <FontAwesomeIcon className="CPSS-ratingIcon" icon={faStar} size="lg" />
                {instructor.instructor_stats.feedbacks.average_rating.toFixed(1)}
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderInclusion(key, label) {
    return (
      <Typography key={key} sx={{ color: isExpired ? 'text.disabled' : 'text.primary' }}>
        <FontAwesomeIcon icon={faCheck} />
        {label}
      </Typography>
    );
  }

  function renderRegisteredActions() {
    if (studySession.type === 'weekly_tutorial') {
      const curDate = new Date();
      const prevSession = studySession.dates.findLast(d => new Date(d.end_time) < curDate);
      const nextSession = studySession.dates.find(d => new Date(d.end_time) > curDate);
      if (nextSession) {
        return [prevSession, nextSession].filter(Boolean).map((ssd, i, a) => {
          const isUpcoming = i === a.length - 1;
          return (
            <div
              key={ssd.id}
              css={[{ display: 'flex', flexWrap: 'wrap' }, !i && (({ spacing }) => ({ marginTop: spacing(2) }))]}
            >
              <div key={ssd.id} className="CPSS-datePart" css={{ minWidth: 200 }}>
                <FontAwesomeIcon icon={isUpcoming ? faCalendar : faCalendarCheck} />
                <div>
                  <Typography variant="subtitle1">{isUpcoming ? 'Upcoming session' : 'Previous session'}</Typography>
                  <Typography variant="body2">
                    {formatDateRange(ssd.start_time, ssd.end_time, 'upcomingDateAndTime')}
                  </Typography>
                </div>
              </div>
              <StudySessionRegisteredActions
                course={course}
                studySession={studySession}
                studySessionDate={ssd}
                studySessionUser={studySessionUser}
              />
            </div>
          );
        });
      }
      if (!studySession.attachments?.some(a => !a.ssd_id)) return;
    }
    return (
      <StudySessionRegisteredActions
        allDates
        course={course}
        css={({ spacing }) => ({ marginTop: spacing(2) })}
        dateStatus={dateStatus}
        studySession={studySession}
        studySessionUser={studySessionUser}
      />
    );
  }

  function renderPriceBlock() {
    const isDiscounted =
      studySession.purchase_type === 'standard' &&
      !!studySession.regular_price &&
      studySession.regular_price > studySession.price;
    const subscriptionAccess = studySession.purchase_type === 'subscription';
    const courseBundleAccess = studySession.purchase_type === 'course_bundle';
    return (
      <div className="CPSS-priceBlock">
        {(subscriptionAccess || courseBundleAccess || isDiscounted) && (
          <Typography
            align="center"
            sx={{
              color: 'text.secondary',
            }}
            variant="overline"
          >
            {subscriptionAccess ?
              `Included with ${WIZE_SUBSCRIPTION_NAME}`
            : courseBundleAccess ?
              'Included with Course purchase'
            : studySession.price ?
              'early bird'
            : 'free access'}
          </Typography>
        )}
        {studySession.purchase_type === 'standard' && (
          <div className="CPSS-price">
            <Typography variant="h5">{numberToPrice(studySession.price)}</Typography>
            {isDiscounted && (
              <Typography style={{ textDecoration: 'line-through' }} variant="body2">
                {numberToPrice(studySession.regular_price)}
              </Typography>
            )}
          </div>
        )}
      </div>
    );
  }

  function renderExtraDetails() {
    return (
      <div className="CPSS-extraDetails">
        {studySession.type === 'weekly_tutorial' && (
          <>
            {studySession.dates.map(studySessionDate => {
              const { end_time, id, start_time } = studySessionDate;
              const dateStatus = getStudySessionDateDateStatus(studySessionDate, studySession);

              return (
                <div key={id}>
                  <Typography
                    className={classNames('CPSS-datePart', { 'CPSS-datePart_past': dateStatus === 'past' })}
                    variant="subtitle1"
                  >
                    <FontAwesomeIcon icon={faCalendar} />
                    {formatDateRange(start_time, end_time, 'upcomingDateDayAndTime')}
                  </Typography>
                  {showRegistreredActions && (
                    <StudySessionRegisteredActions
                      course={course}
                      dateStatus={dateStatus}
                      studySession={studySession}
                      studySessionDate={studySessionDate}
                      studySessionUser={studySessionUser}
                    />
                  )}
                </div>
              );
            })}
          </>
        )}
        {hasContent(studySession.extra_details) && (
          <Editor plugins={pluginPresets.ALL} readOnly value={studySession.extra_details} />
        )}
      </div>
    );
  }
});
